// lib/gpt-image-25.ts
import type { GptImage25Mode, GptImage25Params, GptImage25Quality } from '#types/types';

export type { GptImage25Mode, GptImage25Params, GptImage25Quality };

export const GPT_IMAGE_25_CANVAS_REFERENCE_MAX = 10;

export const GPT_IMAGE_25_DEFAULT_PARAMS: GptImage25Params = {
  prompt: '',
  mode: 'sunburst',
  quality: 'medium',
};

export const GPT_IMAGE_25_QUALITY_CREDITS: Readonly<Record<GptImage25Quality, number>> = {
  low: 1,
  medium: 1.5,
  high: 2,
  xhigh: 2.25,
  max: 2.5,
};

export const GPT_IMAGE_25_SCHEMA_FINGERPRINT = '8fc95df0b13a93def9b165aa8cb5ecd31c339ee2485039c72ae3a40f6e007367';
export const GPT_IMAGE_25_SCHEMA_TRANSCRIPTION = {
  background: ['auto', 'opaque', 'transparent'],
  deployment: '7ec3df5638714ae94442a-production',
  editOptional: ['mask_url'],
  editRequired: ['image_urls'],
  endpoints: [
    'openai/gpt-image-2.5/flare/edit',
    'openai/gpt-image-2.5/flare/text-to-image',
    'openai/gpt-image-2.5/sunburst/edit',
    'openai/gpt-image-2.5/sunburst/text-to-image',
  ],
  imageFileRequired: ['url'],
  imageSize: {
    custom: { height: 'integer', width: 'integer' },
    observedCustomMaxEdge: 14142,
    presets: ['auto', 'landscape_4_3', 'landscape_16_9', 'portrait_4_3', 'portrait_16_9', 'square', 'square_hd'],
  },
  num_images: { default: 1, type: 'integer' },
  output_format: ['jpeg', 'png', 'webp'],
  productOutputCaps: { maxAspectRatio: 3, maxEdge: 3840, maxPixels: 8294400, minPixels: 655360 },
  prompt: { maxLength: 32000, type: 'string' },
  quality: ['auto', 'high', 'low', 'max', 'medium', 'xhigh'],
} as const;

export const GPT_IMAGE_25_ALLOWED_PARAM_KEYS = ['prompt', 'mode', 'quality'] as const;

export const isGptImage25ApplicationContextKey = (key: string): boolean => (
  key.startsWith('list') ||
  key.startsWith('factory') ||
  key.startsWith('inheritSource') ||
  key === 'generationTaskId'
);

export const normalizeGptImage25Params = (value: unknown): GptImage25Params => {
  const source = value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
  const prompt = typeof source.prompt === 'string' ? source.prompt.trim() : '';
  const mode = source.mode === 'flare' || source.mode === 'sunburst' ? source.mode : 'sunburst';
  const quality = source.quality === 'low' || source.quality === 'medium' || source.quality === 'high'
    || source.quality === 'xhigh' || source.quality === 'max' ? source.quality : 'medium';
  return { prompt, mode, quality };
};

export const assertGptImage25Prompt = (prompt: string): string => {
  const effectivePrompt = prompt.trim();
  if (!effectivePrompt) throw new Error('GPT-Image-2.5 requires a non-empty prompt.');
  if (Array.from(effectivePrompt).length > 32000) throw new Error('GPT-Image-2.5 prompts cannot exceed 32,000 characters.');
  return effectivePrompt;
};

export const getGptImage25Endpoint = (mode: GptImage25Mode, hasSource: boolean): string => (
  `openai/gpt-image-2.5/${mode}/${hasSource ? 'edit' : 'text-to-image'}`
);

export const buildGptImage25ProviderInput = (input: {
  params: GptImage25Params;
  width: number;
  height: number;
  imageUrls?: readonly string[];
  sourceUrl?: string;
}) => {
  const imageUrls = [
    ...(input.sourceUrl ? [input.sourceUrl] : []),
    ...(input.imageUrls ?? []),
  ].filter((url, index, urls): url is string => typeof url === 'string' && url.trim().length > 0 && urls.indexOf(url) === index);
  return {
    prompt: input.params.prompt,
    image_size: { width: input.width, height: input.height },
    background: 'auto' as const,
    quality: input.params.quality,
    num_images: 1,
    output_format: 'webp' as const,
    ...(imageUrls.length > 0 ? { image_urls: imageUrls } : {}),
  };
};

export const getGptImage25QualityCredits = (quality: unknown): number => {
  const normalized = normalizeGptImage25Params({ quality }).quality;
  return GPT_IMAGE_25_QUALITY_CREDITS[normalized];
};

export interface GptImage25OutputGeometry {
  width: number;
  height: number;
  baseWidth: number;
  baseHeight: number;
  scale: number;
}

export const resolveGptImage25OutputGeometry = (rect: unknown): GptImage25OutputGeometry => {
  if (!rect || typeof rect !== 'object' || Array.isArray(rect)) throw new Error('GPT-Image-2.5 requires a persisted generation area.');
  const raw = rect as Record<string, unknown>;
  if (![raw.w, raw.h].every(value => typeof value === 'number' && Number.isFinite(value) && value > 0)) {
    throw new Error('GPT-Image-2.5 requires a finite, positive generation area.');
  }
  const baseWidth = Math.round(raw.w as number);
  const baseHeight = Math.round(raw.h as number);
  if (baseWidth <= 0 || baseHeight <= 0) throw new Error('GPT-Image-2.5 requires a finite, positive generation area.');
  const ratio = baseWidth / baseHeight;
  if (ratio < 1 / 3 || ratio > 3) throw new Error('GPT-Image-2.5 supports generation areas between 1:3 and 3:1.');
  // Workspace coordinates are virtual layout units, not source pixels. The browser render
  // already asks renderWorkspaceArea for the provider budget; derive wire dimensions from the
  // persisted aspect ratio only, otherwise a small on-screen area is mistaken for a tiny image.
  const maxPixels = 8_294_400;
  const maxEdge = 3840;
  const multiple = 16;
  const scale = ratio >= 1
    ? Math.min(maxEdge / baseWidth, Math.sqrt(maxPixels * ratio) / baseWidth)
    : Math.min(maxEdge / baseHeight, Math.sqrt(maxPixels / ratio) / baseHeight);
  const width = Math.max(multiple, Math.floor(baseWidth * scale / multiple) * multiple);
  const height = Math.max(multiple, Math.floor(baseHeight * scale / multiple) * multiple);
  const pixels = width * height;
  if (pixels < 655_360 || pixels > maxPixels || Math.max(width, height) > maxEdge) {
    throw new Error('GPT-Image-2.5 could not resolve a supported output size.');
  }
  const outputRatio = width / height;
  if (outputRatio < 1 / 3 || outputRatio > 3) throw new Error('GPT-Image-2.5 output ratio is unsupported.');
  return { width, height, baseWidth, baseHeight, scale };
};

export const validateGptImage25OutputDimensions = (width: number, height: number): { width: number; height: number } => {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0
    || !Number.isInteger(width) || !Number.isInteger(height) || width % 16 !== 0 || height % 16 !== 0) {
    throw new Error('GPT-Image-2.5 output dimensions must be positive multiples of 16.');
  }
  const pixels = width * height;
  const ratio = width / height;
  if (pixels < 655_360 || pixels > 8_294_400 || Math.max(width, height) > 3840 || ratio < 1 / 3 || ratio > 3) {
    throw new Error('GPT-Image-2.5 output dimensions are outside the supported provider range.');
  }
  return { width, height };
};

export const assertStrictBase64Payload = (payload: string): void => {
  if (!payload || payload.length % 4 !== 0 || !/^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/.test(payload)) {
    throw new Error('Invalid generated image encoding.');
  }
  const decoded = Buffer.from(payload, 'base64');
  if (!decoded.length || decoded.toString('base64') !== payload) throw new Error('Invalid generated image encoding.');
};

